import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Package, ShoppingCart, Clock, DollarSign, AlertTriangle, 
  ChevronRight, RefreshCw, Plus, Edit, Trash2, Check, X,
  ArrowLeft, LayoutDashboard, Box, ClipboardList, CalendarDays, LogOut, Lock, Settings as SettingsIcon
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import AdminSettings from "@/components/AdminSettings";

const TOKEN_KEY = "gs_admin_token";

// Convert FastAPI error `detail` (which may be string, array, or object) into a string
// so React never tries to render an object as a child.
function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

/** Admin-scoped axios instance — attaches JWT + sends cookies. */
function useAdminAxios(onUnauthorized) {
  return useMemo(() => {
    const instance = axios.create({ baseURL: API, withCredentials: true });
    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        config.headers = config.headers || {};
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    });
    instance.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          if (onUnauthorized) onUnauthorized();
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [onUnauthorized]);
}

/** Login form shown when the admin is not authenticated. */
function AdminLoginGate({ onLoggedIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (data?.token) localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Signed in");
      onLoggedIn();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 border">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 mx-auto mb-4">
          <Lock className="h-5 w-5 text-emerald-700" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">
          Admin Sign In
        </h1>
        <p className="text-sm text-slate-500 text-center mb-6">
          Restricted to Gorton Superstore staff.
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
          <div>
            <Label htmlFor="admin-email">Email</Label>
            <Input
              id="admin-email"
              type="email"
              value={email}
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <Label htmlFor="admin-password">Password</Label>
            <Input
              id="admin-password"
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-login-password"
            />
          </div>
          {error && (
            <div
              className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="admin-login-submit"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-slate-500 hover:text-emerald-600">
            ← Back to store
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [authState, setAuthState] = useState("checking"); // "checking" | "in" | "out"

  const handleUnauthorized = () => setAuthState("out");
  const admin = useAdminAxios(handleUnauthorized);

  // Verify session on mount using /auth/me
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setAuthState("out");
      return;
    }
    admin
      .get("/auth/me")
      .then(() => setAuthState("in"))
      .catch(() => setAuthState("out"));
  }, [admin]);

  if (authState === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading…
      </div>
    );
  }
  if (authState === "out") {
    return <AdminLoginGate onLoggedIn={() => setAuthState("in")} />;
  }

  return <AdminDashboardInner admin={admin} onLogout={() => setAuthState("out")} />;
}

function AdminDashboardInner({ admin, onLogout }) {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Product form state
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    category_id: "",
    price: "",
    unit: "kg",
    stock: "",
    is_special: false,
    special_price: "",
  });

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ordersRes, productsRes, categoriesRes] = await Promise.all([
        admin.get(`/admin/stats`),
        admin.get(`/orders`),
        admin.get(`/products`),
        admin.get(`/categories`),
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    try {
      await admin.post("/auth/logout");
    } catch (e) {
      /* ignore — clear locally anyway */
    }
    localStorage.removeItem(TOKEN_KEY);
    toast.success("Signed out");
    onLogout();
  };

  // Update order status
  const updateOrderStatus = async (orderId, status) => {
    try {
      await admin.patch(`/orders/${orderId}/status`, { status });
      toast.success(`Order marked as ${status}`);
      fetchData();
    } catch (e) {
      toast.error("Failed to update order");
    }
  };

  // Mark bank transfer order as paid
  const markOrderPaid = async (orderId) => {
    try {
      await admin.patch(`/orders/${orderId}/mark-paid`);
      toast.success("Payment marked as received. Customer has been notified.");
      fetchData();
    } catch (e) {
      toast.error("Failed to mark as paid");
    }
  };

  // Update product stock
  const updateStock = async (productId, stock) => {
    try {
      await admin.patch(`/products/${productId}/stock`, { stock: parseInt(stock) });
      toast.success("Stock updated");
      fetchData();
    } catch (e) {
      toast.error("Failed to update stock");
    }
  };

  // Save product
  const saveProduct = async () => {
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock: parseInt(productForm.stock),
        special_price: productForm.is_special && productForm.special_price 
          ? parseFloat(productForm.special_price) 
          : null,
      };

      if (editingProduct) {
        await admin.put(`/products/${editingProduct.id}`, data);
        toast.success("Product updated");
      } else {
        await admin.post(`/products`, data);
        toast.success("Product created");
      }
      
      setEditingProduct(null);
      setProductForm({
        name: "",
        description: "",
        category_id: "",
        price: "",
        unit: "kg",
        stock: "",
        is_special: false,
        special_price: "",
      });
      fetchData();
    } catch (e) {
      toast.error("Failed to save product");
    }
  };

  // Delete product
  const deleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await admin.delete(`/products/${productId}`);
      toast.success("Product deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete product");
    }
  };

  // Edit product
  const startEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description || "",
      category_id: product.category_id,
      price: product.price.toString(),
      unit: product.unit,
      stock: product.stock.toString(),
      is_special: product.is_special,
      special_price: product.special_price?.toString() || "",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-amber-100 text-amber-700",
      confirmed: "bg-blue-100 text-blue-700",
      preparing: "bg-purple-100 text-purple-700",
      ready: "bg-emerald-100 text-emerald-700",
      collected: "bg-slate-100 text-slate-700",
      cancelled: "bg-red-100 text-red-700",
    };
    return colors[status] || "bg-slate-100 text-slate-700";
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-slate-500 hover:text-slate-700">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Admin Dashboard</h1>
                <p className="text-sm text-slate-500">Gorton Superstore</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="admin-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">
              <ClipboardList className="h-4 w-4 mr-2" />
              Orders
            </TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">
              <Box className="h-4 w-4 mr-2" />
              Products
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">
              <SettingsIcon className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="stat-card">
                <CardHeader className="pb-2">
                  <CardDescription>Total Orders</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-3xl font-bold" data-testid="stat-total-orders">
                      {stats?.total_orders || 0}
                    </span>
                    <ShoppingCart className="h-8 w-8 text-emerald-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="stat-card">
                <CardHeader className="pb-2">
                  <CardDescription>Today's Pickups</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-3xl font-bold" data-testid="stat-todays-orders">
                      {stats?.todays_orders || 0}
                    </span>
                    <Clock className="h-8 w-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="stat-card">
                <CardHeader className="pb-2">
                  <CardDescription>Total Revenue</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-3xl font-bold" data-testid="stat-revenue">
                      £{(stats?.total_revenue || 0).toFixed(0)}
                    </span>
                    <DollarSign className="h-8 w-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card className="stat-card">
                <CardHeader className="pb-2">
                  <CardDescription>Low Stock Items</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <span className="text-3xl font-bold text-amber-600" data-testid="stat-low-stock">
                      {stats?.low_stock_products || 0}
                    </span>
                    <AlertTriangle className="h-8 w-8 text-amber-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Order Status Summary */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Order Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                      <span className="font-medium text-amber-700">Pending</span>
                      <Badge className="bg-amber-100 text-amber-700">{stats?.pending_orders || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="font-medium text-blue-700">Confirmed</span>
                      <Badge className="bg-blue-100 text-blue-700">{stats?.confirmed_orders || 0}</Badge>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                      <span className="font-medium text-emerald-700">Ready for Pickup</span>
                      <Badge className="bg-emerald-100 text-emerald-700">{stats?.ready_orders || 0}</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {orders.slice(0, 5).map((order) => (
                      <div key={order.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">#{order.id.slice(0, 8)}</p>
                          <p className="text-xs text-slate-500">{order.customer_name}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={getStatusColor(order.order_status)}>
                            {order.order_status}
                          </Badge>
                          <p className="text-sm font-medium mt-1">£{order.total.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>All Orders</CardTitle>
                <CardDescription>Manage and update order statuses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Order ID</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Pickup</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {orders.map((order) => (
                        <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                          <TableCell className="font-mono text-sm">
                            #{order.id.slice(0, 8)}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{order.customer_name}</p>
                              <p className="text-xs text-slate-500">{order.customer_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>{order.items.length} items</TableCell>
                          <TableCell className="font-medium">£{order.total.toFixed(2)}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p>{order.pickup_date}</p>
                              <p className="text-slate-500">{order.pickup_time}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <span className="text-xs text-slate-600">
                                {order.payment_method === "bank_transfer" && "Bank Transfer"}
                                {order.payment_method === "pay_at_pickup" && "Pay at Pickup"}
                                {order.payment_method === "stripe" && "Card"}
                              </span>
                              <Badge
                                className={
                                  order.payment_status === "paid"
                                    ? "bg-emerald-100 text-emerald-700 w-fit"
                                    : "bg-amber-100 text-amber-700 w-fit"
                                }
                                data-testid={`payment-status-${order.id}`}
                              >
                                {order.payment_status === "paid" ? "Paid" : "Pending"}
                              </Badge>
                              {order.payment_method === "bank_transfer" && order.payment_status !== "paid" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-1 h-7 text-xs border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => markOrderPaid(order.id)}
                                  data-testid={`mark-paid-${order.id}`}
                                >
                                  Mark as Paid
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(order.order_status)}>
                              {order.order_status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={order.order_status}
                              onValueChange={(val) => updateOrderStatus(order.id, val)}
                            >
                              <SelectTrigger className="w-32" data-testid={`status-select-${order.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="preparing">Preparing</SelectItem>
                                <SelectItem value="ready">Ready</SelectItem>
                                <SelectItem value="collected">Collected</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Products</CardTitle>
                  <CardDescription>Manage inventory and product details</CardDescription>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => {
                        setEditingProduct(null);
                        setProductForm({
                          name: "",
                          description: "",
                          category_id: categories[0]?.id || "",
                          price: "",
                          unit: "kg",
                          stock: "",
                          is_special: false,
                          special_price: "",
                        });
                      }}
                      data-testid="add-product-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Product
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={productForm.name}
                          onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                          data-testid="product-name-input"
                        />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select
                          value={productForm.category_id}
                          onValueChange={(val) => setProductForm({ ...productForm, category_id: val })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Price (£)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={productForm.price}
                            onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                            data-testid="product-price-input"
                          />
                        </div>
                        <div>
                          <Label>Unit</Label>
                          <Select
                            value={productForm.unit}
                            onValueChange={(val) => setProductForm({ ...productForm, unit: val })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="kg">kg</SelectItem>
                              <SelectItem value="each">each</SelectItem>
                              <SelectItem value="pack">pack</SelectItem>
                              <SelectItem value="bottle">bottle</SelectItem>
                              <SelectItem value="bag">bag</SelectItem>
                              <SelectItem value="loaf">loaf</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Stock</Label>
                        <Input
                          type="number"
                          value={productForm.stock}
                          onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                          data-testid="product-stock-input"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="is_special"
                          checked={productForm.is_special}
                          onChange={(e) => setProductForm({ ...productForm, is_special: e.target.checked })}
                          className="rounded"
                        />
                        <Label htmlFor="is_special">Weekly Special</Label>
                      </div>
                      {productForm.is_special && (
                        <div>
                          <Label>Special Price (£)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={productForm.special_price}
                            onChange={(e) => setProductForm({ ...productForm, special_price: e.target.value })}
                          />
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <Button onClick={saveProduct} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-product-btn">
                        {editingProduct ? "Update Product" : "Add Product"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Stock</TableHead>
                        <TableHead>Special</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map((product) => (
                        <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {product.image_url && (
                                <img
                                  src={product.image_url}
                                  alt={product.name}
                                  className="w-10 h-10 rounded-lg object-cover"
                                />
                              )}
                              <div>
                                <p className="font-medium">{product.name}</p>
                                <p className="text-xs text-slate-500">{product.unit}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {categories.find((c) => c.id === product.category_id)?.name || "-"}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">£{product.price.toFixed(2)}</p>
                              {product.special_price && (
                                <p className="text-xs text-red-600">Sale: £{product.special_price.toFixed(2)}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                value={product.stock}
                                onChange={(e) => updateStock(product.id, e.target.value)}
                                className="w-20"
                                data-testid={`stock-input-${product.id}`}
                              />
                              {product.stock < 10 && (
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {product.is_special ? (
                              <Badge className="bg-red-100 text-red-700">On Sale</Badge>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => startEditProduct(product)}
                                    data-testid={`edit-product-${product.id}`}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Edit Product</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4 py-4">
                                    <div>
                                      <Label>Name</Label>
                                      <Input
                                        value={productForm.name}
                                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                                      />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <Label>Price (£)</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={productForm.price}
                                          onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                                        />
                                      </div>
                                      <div>
                                        <Label>Stock</Label>
                                        <Input
                                          type="number"
                                          value={productForm.stock}
                                          onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                                        />
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        id="edit_is_special"
                                        checked={productForm.is_special}
                                        onChange={(e) => setProductForm({ ...productForm, is_special: e.target.checked })}
                                        className="rounded"
                                      />
                                      <Label htmlFor="edit_is_special">Weekly Special</Label>
                                    </div>
                                    {productForm.is_special && (
                                      <div>
                                        <Label>Special Price (£)</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={productForm.special_price}
                                          onChange={(e) => setProductForm({ ...productForm, special_price: e.target.value })}
                                        />
                                      </div>
                                    )}
                                  </div>
                                  <DialogFooter>
                                    <Button onClick={saveProduct} className="bg-emerald-600 hover:bg-emerald-700">
                                      Update Product
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => deleteProduct(product.id)}
                                data-testid={`delete-product-${product.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <AdminSettings admin={admin} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
